import { useEffect } from 'react'
import {
  abandonWearFunnelSession,
  emitWearFunnelEvent,
  retainWearFunnelSession,
} from '../lib/wearFunnelTelemetry'

export function useWearFunnelLifecycle(session) {
  useEffect(() => retainWearFunnelSession(session), [session])

  useEffect(() => {
    if (!session) return
    const onPageHide = () => {
      abandonWearFunnelSession(session, { reason: 'pagehide' })
    }
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        abandonWearFunnelSession(session, { reason: 'backgrounded' })
      }
    }
    window.addEventListener('pagehide', onPageHide)
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      window.removeEventListener('pagehide', onPageHide)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [session])
}

export function useWearConfirmationVisibility(session, elementRef, fragranceId, enabled = true) {
  useEffect(() => {
    if (!session || !enabled) return
    const element = elementRef.current
    if (!element) return

    const emitIfVisible = () => {
      const rect = element.getBoundingClientRect()
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight
      const visible = rect.width > 0
        && rect.height > 0
        && rect.bottom > 0
        && rect.right > 0
        && rect.top < viewportHeight
        && rect.left < viewportWidth
      if (visible) {
        emitWearFunnelEvent(session, 'confirm_shown', {
          fragranceId,
          meta: {
            viewport_height: viewportHeight,
            element_top: Math.round(rect.top),
            element_bottom: Math.round(rect.bottom),
          },
        })
      }
      return visible
    }

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        if (entries.some(entry => entry.isIntersecting && entry.intersectionRect.height > 0)) {
          emitIfVisible()
        }
      })
      observer.observe(element)
      return () => observer.disconnect()
    }

    const onViewportChange = () => emitIfVisible()
    window.addEventListener('scroll', onViewportChange, { passive: true })
    window.addEventListener('resize', onViewportChange)
    onViewportChange()
    return () => {
      window.removeEventListener('scroll', onViewportChange)
      window.removeEventListener('resize', onViewportChange)
    }
  }, [session, elementRef, fragranceId, enabled])
}
